<template>
    <div>
        <div
            v-if="hasContent"
            class="relative"
            :class="
                hasOverflowingContent
                    ? moreShown
                        ? ''
                        : 'max-h-[320px] overflow-hidden'
                    : ''
            "
        >
            <div
                ref="contentDiv"
                class="space-y-4 border-t px-2 pt-2"
            >
                <div class="inline-block w-full">
                    <div
                        v-if="presentationData?.bookingImage !== null"
                        class="flex justify-center py-4"
                        :class="
                            (presentationData?.bookingPageHeader ?? '') !== ''
                                ? 'sm:float-right sm:mb-6 sm:ml-6'
                                : ''
                        "
                    >
                        <img
                            :src="presentationData?.bookingImage"
                            class="max-w-[300px]"
                            style="box-shadow: 0 0 5px 2px rgb(0 0 0 / 0.3)"
                            :alt="presentationData?.title"
                        />
                    </div>

                    <content-reset
                        :body="presentationData?.bookingPageHeader ?? ''"
                    />
                </div>

                <tab-view
                    v-if="presentationData?.bookingPageSections.length"
                    @tab-click="moreShown = true"
                >
                    <tab-panel
                        v-for="section in presentationData?.bookingPageSections"
                        :key="section.title"
                        :header="section.title"
                        :pt="{
                            headerTitle: {
                                class: 'uppercase tracking-wider text-gray-500'
                            }
                        }"
                    >
                        <content-reset :body="section.body" />
                    </tab-panel>
                </tab-view>

                <content-reset
                    :body="presentationData?.bookingPageFooter ?? ''"
                />
            </div>

            <div
                v-if="hasOverflowingContent && !moreShown"
                class="pointer-events-none absolute -bottom-2 left-0 right-0 h-32 bg-gradient-to-b from-white/10 to-white blur-sm"
            />
        </div>

        <prime-button
            v-if="hasOverflowingContent && !moreShown"
            outlined
            class="mt-2"
            severity="secondary"
            @click="moreShown = true"
        >
            {{ t('showMore') }}
        </prime-button>
    </div>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const PlanPresentationPlanFragment = graphql(/** GraphQL */ `
    fragment PlanPresentationPlanFragment on Plan {
        name
        presentation(input: $input, language: $language) {
            ...PlanPresentationSeoFragment
            disableShowMore
        }
    }
`)
</script>

<script setup lang="ts">
import TabPanel from 'primevue/tabpanel'
import TabView from 'primevue/tabview'
import type { PropType } from 'vue'
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData } from '~/resources/graphql'
import { computed, ref } from 'vue'
import PrimeButton from 'primevue/button'
import { useElementSize } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import {
    PlanPresentationSeoFragment,
    usePlanPresentationSeo
} from '~/composables/use-plan-presentation-seo'
import ContentReset from '~/components/ContentReset.vue'

const props = defineProps({
    plan: {
        type: Object as PropType<
            FragmentType<typeof PlanPresentationPlanFragment>
        >,
        required: true
    },
    allowBookings: {
        type: Boolean,
        default: false
    }
})

const { t } = useI18n({ useScope: 'global' })
const planData = computed(() => {
    return getFragmentData(PlanPresentationPlanFragment, props.plan)
})
const presentationData = computed(() => {
    if (planData.value.presentation === null) {
        return null
    }

    return getFragmentData(
        PlanPresentationSeoFragment,
        planData.value.presentation
    )
})
const planTitle = computed(() => {
    if (
        presentationData.value !== null &&
        presentationData.value.title !== ''
    ) {
        return presentationData.value.title
    }

    return planData.value.name
})
usePlanPresentationSeo(
    planTitle,
    computed(() => planData.value.presentation)
)

let initialShowMore = false
if (!props.allowBookings) {
    initialShowMore = true
} else if (
    planData.value.presentation !== null &&
    planData.value.presentation.disableShowMore
) {
    initialShowMore = true
}
const moreShown = ref(initialShowMore)
const hasContent = computed(() => {
    if (presentationData.value === null) {
        return false
    }

    if (presentationData.value.bookingPageHeader !== '') {
        return true
    }

    if (presentationData.value.bookingPageFooter !== '') {
        return true
    }

    if (presentationData.value.bookingPageSections.length > 0) {
        return true
    }

    return false
})

const contentDiv = ref<HTMLDivElement>()
const { height } = useElementSize(contentDiv)
const hasOverflowingContent = computed(() => {
    return (
        hasContent.value &&
        (contentDiv.value === undefined || height.value > 320)
    )
})
</script>
